/*!
 * @Instruduction: AI 功能绑定管理服务接口
 * 对应需求文档 §二 接口契约
 * 路径前缀：/api/ai/feature
 *
 * 注：后端 Spring Boot + Jackson 默认返回驼峰命名（featureKey 等），
 * 本服务层统一转换为前端下划线命名（feature_key 等），与模块1 保持一致。
 */

import xhr from './xhr/index'
import {
    IGetFeatureListParams,
    IUpsertFeatureParams,
    IFeatureKey,
    IAvailableProvider
} from '@/types/ai-types'

const BASE = '/api/ai/feature'

/** 后端驼峰 → 前端下划线 字段映射表（模块2 专用） */
const CAMEL_TO_SNAKE_MAP: Record<string, string> = {
    featureKey: 'feature_key',
    featureName: 'feature_name',
    providerId: 'provider_id',
    providerName: 'provider_name',
    providerType: 'provider_type',
    promptTemplate: 'prompt_template',
    modelConfig: 'model_config',
    isDefault: 'is_default',
    createdAt: 'created_at',
    updatedAt: 'updated_at',
    createdBy: 'created_by',
    updatedBy: 'updated_by',
    pageTotal: 'page_total',
    pageIndex: 'page_index',
    defaultValue: 'default_value',
    defaultOption: 'default_option'
}

/** 将对象的驼峰字段批量转为下划线（仅映射表中声明的字段），递归处理嵌套对象和数组 */
function camelToSnake<T = any>(obj: any): T {
    if (!obj || typeof obj !== 'object') return obj
    if (Array.isArray(obj)) {
        return obj.map(item => camelToSnake(item)) as any
    }
    const result: any = {}
    for (const key of Object.keys(obj)) {
        const snakeKey = CAMEL_TO_SNAKE_MAP[key] || key
        result[snakeKey] = camelToSnake(obj[key])
    }
    return result as T
}

/** 转换列表接口的响应体 */
function transformListResponse(raw: any) {
    if (!raw) return raw
    // 后端分页字段是 rows（而非模块1 的 list），同时兼容 list
    const rows = raw.rows || raw.list || []
    return {
        ...raw,
        total: raw.total,
        page_index: raw.pageIndex != null ? raw.pageIndex : raw.page_index,
        page_size: raw.pageSize != null ? raw.pageSize : raw.page_size,
        rows: rows.map((item: any) => camelToSnake(item))
    }
}

/** 转换详情/单条响应 */
function transformItemResponse(raw: any) {
    if (!raw) return raw
    return camelToSnake(raw)
}

class AiFeatureService {
    /** 功能绑定列表（分页 + 关键词 + 状态过滤） */
    public async listFeatures(params: IGetFeatureListParams) {
        const result = await xhr({
            method: 'get',
            url: `${BASE}/list`,
            params: params
        })
        if (result.status === 200 && result.data) {
            result.data = transformListResponse(result.data)
        }
        return result
    }

    /** 功能绑定详情 */
    public async getFeature(id: number) {
        const result = await xhr({
            method: 'get',
            url: `${BASE}/${id}`,
            params: {}
        })
        if (result.status === 200 && result.data) {
            result.data = transformItemResponse(result.data)
        }
        return result
    }

    /** 新建功能绑定 */
    public async createFeature(params: IUpsertFeatureParams) {
        const result = await xhr({
            method: 'post',
            url: BASE,
            params: params
        })
        return result
    }

    /** 编辑功能绑定 */
    public async updateFeature(params: IUpsertFeatureParams) {
        const result = await xhr({
            method: 'put',
            url: BASE,
            params: params
        })
        return result
    }

    /** 删除功能绑定 */
    public async deleteFeature(id: number) {
        const result = await xhr({
            method: 'delete',
            url: `${BASE}/${id}`,
            params: {}
        })
        return result
    }

    /** 启用/禁用（URL 参数传 status） */
    public async setFeatureStatus(id: number, status: number) {
        const result = await xhr({
            method: 'put',
            url: `${BASE}/${id}/status`,
            params: { status }
        })
        return result
    }

    /** 功能标识枚举下拉（feature_keys） */
    public async listFeatureKeys() {
        const result = await xhr({
            method: 'get',
            url: `${BASE}/feature-keys`,
            params: {}
        })
        if (result.status === 200 && result.data) {
            result.data = (result.data || []).map((i: any) => ({
                key: i.key,
                label: i.label
            }))
        }
        return result
    }

    /** 可绑定模型下拉列表 */
    public async listAvailableProviders() {
        const result = await xhr({
            method: 'get',
            url: `${BASE}/available-providers`,
            params: {}
        })
        if (result.status === 200 && result.data) {
            result.data = (result.data || []).map((i: any) => ({
                id: i.id,
                name: i.name,
                type: i.type,
                model: i.model
            }))
        }
        return result
    }

    /** 根据模型获取参数 Schema（动态渲染表单） */
    public async getConfigSchema(providerId: number) {
        const result = await xhr({
            method: 'get',
            url: `${BASE}/config-schema`,
            params: { providerId }
        })
        if (result.status === 200 && result.data) {
            result.data = transformItemResponse(result.data)
        }
        return result
    }
}

// 实例化后导出，全局单例
export default new AiFeatureService()
